/**
 * questionari/provenienza — da dove arriva chi compila.
 * ============================================================================
 *
 * La pagina legge `?fonte=linkedin` e i parametri utm dall'indirizzo, più il DOMINIO del sito da
 * cui si è arrivati (mai l'indirizzo completo: può contenere dati di altri). Il server rifà la
 * pulizia con `pulisciProvenienza`, perché la richiesta la scrive il browser.
 *
 * La provenienza sta in un campo a sé, non nella `fonte` del lead (che è un elenco chiuso del CRM):
 * un valore nuovo («newsletter_marzo») non deve chiedere una modifica al codice.
 *
 * File puro, target ES2017.
 */

import type { Provenienza } from './tipi';

const PARAMETRI_UTM = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content'] as const;

/** Un valore pulito: minuscolo, lettere, cifre, trattini e punti; al massimo 80 caratteri. */
function codice(v: unknown): string | null {
  if (typeof v !== 'string') return null;
  const c = v
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9._-]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 80);
  return c || null;
}

function dominio(v: unknown): string | null {
  if (typeof v !== 'string' || !v.trim()) return null;
  const m = /^(?:https?:\/\/)?([^/:?#]+)/i.exec(v.trim());
  if (!m) return null;
  const d = m[1].toLowerCase().replace(/^www\./, '');
  return /^[a-z0-9.-]{1,253}$/.test(d) ? d : null;
}

/** `decodeURIComponent` che non lancia: una sequenza rotta nell'indirizzo resta com'è. */
function decodifica(v: string): string {
  try {
    return decodeURIComponent(v.replace(/\+/g, ' '));
  } catch {
    return v;
  }
}

function lingua(v: unknown): string | null {
  const l = typeof v === 'string' ? v.trim().toLowerCase().slice(0, 2) : '';
  return /^[a-z]{2}$/.test(l) ? l : null;
}

/** Ripulisce una provenienza arrivata dal browser (il server non si fida). */
export function pulisciProvenienza(grezza: unknown): Provenienza {
  const g = (grezza && typeof grezza === 'object' ? grezza : {}) as Record<string, unknown>;
  return {
    fonte: codice(g.fonte),
    utm_source: codice(g.utm_source),
    utm_medium: codice(g.utm_medium),
    utm_campaign: codice(g.utm_campaign),
    utm_content: codice(g.utm_content),
    referrer_dominio: dominio(g.referrer_dominio),
    lingua_pagina: lingua(g.lingua_pagina),
  };
}

/**
 * La provenienza dalla pagina: `cerca` è `location.search`, `referrer` è `document.referrer`.
 * Il proprio dominio come provenienza non dice niente, quindi si scarta.
 */
export function provenienzaDallaPagina(cerca: string, referrer: string, linguaPagina: string, proprioDominio?: string): Provenienza {
  const parametri: Record<string, string> = {};
  cerca
    .replace(/^\?/, '')
    .split('&')
    .forEach((coppia) => {
      if (!coppia) return;
      const i = coppia.indexOf('=');
      const chiave = decodifica(i < 0 ? coppia : coppia.slice(0, i));
      const valore = i < 0 ? '' : decodifica(coppia.slice(i + 1));
      parametri[chiave] = valore;
    });
  const grezza: Record<string, unknown> = { fonte: parametri.fonte, lingua_pagina: linguaPagina };
  PARAMETRI_UTM.forEach((p) => {
    grezza[p] = parametri[p];
  });
  const d = dominio(referrer);
  const proprio = proprioDominio ? proprioDominio.toLowerCase().replace(/^www\./, '') : null;
  grezza.referrer_dominio = d && d !== proprio ? d : null;
  return pulisciProvenienza(grezza);
}

/**
 * Il nome con cui una provenienza si conta nelle statistiche: la fonte dichiarata, altrimenti
 * `utm_source`, altrimenti il sito di provenienza, altrimenti «diretto».
 */
export function etichettaProvenienza(p: Partial<Provenienza> | null | undefined): string {
  if (!p) return 'diretto';
  return p.fonte || p.utm_source || p.referrer_dominio || 'diretto';
}
